import Layout from "./Layout/Layout";
import { useRoutes } from "react-router";
import {
  BrowserRouter,
  Navigate,
  RouteObject as RouteInterface,
} from "react-router-dom";
import NotFoundPage from "./Pages/NotFound/NotFoundPage";
import CharacterListPage from "Pages/Character/CharacterListPage";
import "./Assets/css/styles.css";
import React, { useEffect, useState } from "react";
import { ConfigProvider } from "antd";
import SettingPage from "Pages/General/Settings/SettingsPage";

import CharacterCreatePage from "Pages/Character/CharacterCreatePage";
import {
  hasPermission,
  getCurrentAdmin,
  isLoggedIn,
  isSessionExpiredError,
  LOGIN_PATH,
  PERMISSIONS,
  redirectToLogin,
} from "@model";
import Login2page from "Pages/General/Auth/Login/LoginPage";
import DashboardPage from "Pages/Dashboard/DashboardPage";
import UserListPage from "Pages/User/UserListPage";
import UserEditPage from "Pages/User/UserEditPage";
import UserMessagesPage from "Pages/User/UserMessagesPage";
import CharacterMessagesPage from "Pages/Character/CharacterMessagePage";
import HomePageV2 from "Pages/Home/HomePageV2";
import PrivacyPage from "Pages/General/PrivacyPage";
import DeleteAccountPage from "Pages/General/DeleteAccountPage";
import SafetyPage from "Pages/General/SafetyPage";
import ReportListPage from "Pages/Report/ReportListPage";
import PaymentListPage from "Pages/Payment/PaymentListPage";
import PlacementTest from "Pages/PlacementTest/PlacementTest";
import GroupPreviewPage from "Pages/General/GroupPreviewPage";
import UserPreviewPage from "Pages/General/UserPreviewPage";
import WordListPage from "Pages/Word/WordListPage";
import NotificationPage from "Pages/Notification/NotificationPage";
import SurveyPage from "Pages/Survey/SurveyPage";
import ChatLessonListPage from "Pages/ChatLesson/ChatLessonListPage";
import ChatLessonCreatePage from "Pages/ChatLesson/ChatLessonCreatePage";
import JourneyListPage from "Pages/Journey/JourneyListPage";
import JourneyCreatePage from "Pages/Journey/JourneyCreatePage";
import WordCardListPage from "Pages/WordCard/WordCardListPage";
import WordCardCreatePage from "Pages/WordCard/WordCardCreatePage";

export interface RouteObject {
  caseSensitive?: boolean;
  children?: RouteObject[];
  element?: React.ReactNode;
  index?: boolean;
  path?: string;
  id?: string;
  anonymous?: boolean;
}

function PermissionRoute(props: {
  permission: string | string[];
  children: React.ReactNode;
}) {
  const allowed = Array.isArray(props.permission)
    ? props.permission.some((permission) => hasPermission(permission))
    : hasPermission(props.permission);
  if (allowed) return <>{props.children}</>;
  const fallback = hasPermission(PERMISSIONS.dashboardView)
    ? "/panel/dashboard"
    : hasPermission(PERMISSIONS.journeysView)
      ? "/panel/journeys"
      : LOGIN_PATH;
  return <Navigate to={fallback} replace />;
}

const routes: RouteObject[] = [
  {
    path: "/panel",
    element: <Layout />,
    anonymous: true,
    children: [
      {
        id: "dashboard",
        path: "/panel/dashboard",
        element: <PermissionRoute permission={PERMISSIONS.dashboardView}><DashboardPage /></PermissionRoute>,
      },
      {
        id: "characterListRoute",
        path: "/panel/characters",
        element: <PermissionRoute permission={PERMISSIONS.charactersView}><CharacterListPage /></PermissionRoute>,
      },

      {
        id: "userCreateRoute",
        path: "/panel/characters/create/:id?",
        element: <PermissionRoute permission={["characters.create", "characters.edit"]}><CharacterCreatePage /></PermissionRoute>,
      },
      {
        id: "userListRoute",
        path: "/panel/users",
        element: <PermissionRoute permission={PERMISSIONS.usersView}><UserListPage /></PermissionRoute>,
      },
      {
        id: "userEditRoute",
        path: "/panel/users/edit/:id",
        element: <PermissionRoute permission={["users.view", "users.edit"]}><UserEditPage /></PermissionRoute>,
      },
      {
        id: "userMessagesListRoute",
        path: "/panel/users/messages/:userId",
        element: <PermissionRoute permission={PERMISSIONS.usersMessages}><UserMessagesPage /></PermissionRoute>,
      },
      {
        path: "/panel/settings",
        id: "settings",
        element: <PermissionRoute permission={PERMISSIONS.settingsView}><SettingPage /></PermissionRoute>,
      },
      {
        path: "/panel/characters/messages/:characterID",
        id: "CharacterMessagesRoute",
        element: <PermissionRoute permission={PERMISSIONS.charactersView}><CharacterMessagesPage /></PermissionRoute>,
      },
      {
        path: "/panel/reports",
        id: "reportsRoute",
        element: <PermissionRoute permission={PERMISSIONS.reportsView}><ReportListPage /></PermissionRoute>,
      },
      {
        path: "/panel/payments",
        id: "paymentsRoute",
        element: <PermissionRoute permission={PERMISSIONS.paymentsView}><PaymentListPage /></PermissionRoute>,
      },
      {
        path: "/panel/words",
        id: "wordsRoute",
        element: <PermissionRoute permission={PERMISSIONS.wordsView}><WordListPage /></PermissionRoute>,
      },
      {
        path: "/panel/surveys",
        id: "surveysRoute",
        element: <PermissionRoute permission={PERMISSIONS.surveysView}><SurveyPage /></PermissionRoute>,
      },
      {
        path: "/panel/notifications",
        id: "notificationsRoute",
        element: <PermissionRoute permission={PERMISSIONS.notificationsView}><NotificationPage /></PermissionRoute>,
      },
      {
        path: "/panel/chat-lessons",
        id: "chatLessonsRoute",
        element: <PermissionRoute permission={PERMISSIONS.chatLessonsView}><ChatLessonListPage /></PermissionRoute>,
      },
      {
        path: "/panel/chat-lessons/create/:id?",
        id: "chatLessonCreateRoute",
        element: <PermissionRoute permission={["chat-lessons.create", "chat-lessons.edit"]}><ChatLessonCreatePage /></PermissionRoute>,
      },
      {
        path: "/panel/word-cards",
        id: "wordCardsRoute",
        element: <PermissionRoute permission={PERMISSIONS.wordCardsView}><WordCardListPage /></PermissionRoute>,
      },
      {
        path: "/panel/word-cards/create/:id?",
        id: "wordCardCreateRoute",
        element: <PermissionRoute permission={["word-cards.create", "word-cards.edit"]}><WordCardCreatePage /></PermissionRoute>,
      },
      {
        path: "/panel/journeys",
        id: "journeysRoute",
        element: <PermissionRoute permission={PERMISSIONS.journeysView}><JourneyListPage /></PermissionRoute>,
      },
      {
        path: "/panel/journeys/create/:id?",
        id: "journeyCreateRoute",
        element: <PermissionRoute permission={["journeys.create", "journeys.edit"]}><JourneyCreatePage /></PermissionRoute>,
      },
    ],
  },
  {
    path: "/",
    id: "home",
    element: <HomePageV2 />,
    anonymous: true,
  },
  {
    path: "/privacy",
    id: "privacy",
    element: <PrivacyPage />,
    anonymous: true,
  },
  {
    path: "/delete-account",
    id: "deleteAccount",
    element: <DeleteAccountPage />,
    anonymous: true,
  },
  {
    path: "/safety",
    id: "safety",
    element: <SafetyPage />,
    anonymous: true,
  },
  {
    path: "/placement-test",
    id: "placementTest",
    element: <PlacementTest />,
    anonymous: true,
  },
  {
    path: "/group/:id",
    id: "groupPreview",
    element: <GroupPreviewPage />,
    anonymous: true,
  },
  {
    path: "/u/:userId",
    id: "userPreview",
    element: <UserPreviewPage />,
    anonymous: true,
  },
  { path: "/avc/login0", element: <Login2page /> },
  { path: "*", id: "404", element: <NotFoundPage />, anonymous: true },
];

function Router() {
  if (isLoggedIn()) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useRoutes(routes as RouteInterface[]);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useRoutes([
    { path: "/avc/login0", element: <Login2page /> },
    { path: "/panel/*", element: <Login2page /> },
    { path: "*", element: <NotFoundPage /> },
    {
      path: "/",
      id: "home",
      element: <HomePageV2 />,
    },
    {
      path: "/privacy",
      id: "privacy",
      element: <PrivacyPage />,
    },
    {
      path: "/delete-account",
      id: "deleteAccount",
      element: <DeleteAccountPage />,
    },
    {
      path: "/safety",
      id: "safety",
      element: <SafetyPage />,
    },
    {
      path: "/placement-test",
      id: "placementTest",
      element: <PlacementTest />,
    },
    {
      path: "/group/:id",
      id: "groupPreview",
      element: <GroupPreviewPage />,
    },
    {
      path: "/u/:userId",
      id: "userPreview",
      element: <UserPreviewPage />,
    },
  ]);
}

function App() {
  const [, setAdminVersion] = useState(0);
  useEffect(() => {
    ConfigProvider.config({
      theme: { primaryColor: "red" },
    });
  }, []);
  useEffect(() => {
    if (!isLoggedIn()) return;
    getCurrentAdmin()
      .then(() => setAdminVersion((version) => version + 1))
      .catch(() => redirectToLogin());
  }, []);
  return (
    <MyErrorBoundary>
      <BrowserRouter>
        <Router />
      </BrowserRouter>
    </MyErrorBoundary>
  );
}

class MyErrorBoundary extends React.Component<
  {},
  { hasError: boolean; error: any }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: "" };
  }

  static getDerivedStateFromError(error: any) {
    if (isSessionExpiredError(error)) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: any) {
    if (isSessionExpiredError(error)) {
      redirectToLogin();
      return;
    }
    this.setState({ hasError: true, error });
  }

  render() {
    if (this.state.hasError === true) {
      return (
        <>
          <div className="error-bn-box">
            <h1>Something went wrong.</h1>
            <p style={{ textAlign: "center" }}>
              {this.state.error.message} <br />
              <a href="/panel/dashboard">Back to dashboard</a>
            </p>
          </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default App;
